import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUp,
  Ellipsis,
  Inbox,
  Sparkles,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useCoachRail } from "@/context/CoachRailContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";

interface Tab {
  id: string;
  icon: LucideIcon;
  labelKey: string;
  to: string;
  match: (path: string) => boolean;
  /**
   * Sidebar feature id whose visibility rules gate this tab. Dashboard and
   * More are always reachable; Candidates and Team follow the same entitlement,
   * role and remote-config rules as the desktop sidebar, so a phone never
   * shows a tab the router would bounce off (RecruitEnabledGuard,
   * FullAccessGuard, IntelligenceEntitlementGuard).
   */
  featureId?: string;
}

/** Sidebar features that have a bottom tab; the More screen lists the rest. */
export const MOBILE_TAB_FEATURE_IDS = [
  "overview",
  "candidates",
  "teams",
] as const;

const TABS: Tab[] = [
  {
    id: "overview",
    icon: Inbox,
    labelKey: "today",
    to: "/",
    match: (p) => p === "/",
  },
  {
    id: "candidates",
    icon: UsersRound,
    labelKey: "candidates",
    to: "/recruit/candidates",
    match: (p) =>
      p === "/recruit/candidates" || p.startsWith("/recruit/candidates/"),
    featureId: "candidates",
  },
  {
    id: "team",
    icon: Users,
    labelKey: "salesTeam",
    to: "/coach/teams",
    match: (p) =>
      p === "/coach/teams" ||
      p.startsWith("/coach/teams/") ||
      p.startsWith("/coach/members/"),
    featureId: "teams",
  },
  {
    id: "more",
    icon: Ellipsis,
    labelKey: "more",
    to: "/more",
    match: (p) => p === "/more",
  },
];

/**
 * The mobile shell (M1): a bottom tab bar with the Ask pill above it on Today
 * and Candidates. Phone-only (`md:hidden`); rendered as a flex child of the
 * layout so it never overlaps content. "More" routes to the read-only More
 * screen (M6).
 */
export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const { openRail } = useCoachRail();
  const entitled = useIntelligenceAccess();
  const visibleFeatures = useVisibleSidebarNavFeatures();

  const tabs = useMemo(() => {
    const visibleIds = new Set(visibleFeatures.map((f) => f.id));
    return TABS.filter(
      (tab) => !tab.featureId || visibleIds.has(tab.featureId),
    );
  }, [visibleFeatures]);

  const path = location.pathname;
  const showAskPill =
    entitled && (path === "/" || path.startsWith("/recruit/candidates"));

  return (
    <div className="shrink-0 border-t border-border bg-background md:hidden">
      {showAskPill && (
        <div className="px-4 pb-2 pt-2.5">
          {/* One control, one action: the whole pill opens the coach rail. */}
          <button
            type="button"
            onClick={openRail}
            className="flex h-[50px] w-full items-center gap-2 rounded-full border border-border bg-card pl-4 pr-1.5 text-left shadow-warm-sm transition-colors hover:bg-accent focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
          >
            <Sparkles
              aria-hidden="true"
              className="size-[18px] shrink-0 text-primary"
            />
            <span className="min-w-0 flex-1 truncate text-[13.5px] text-muted-foreground">
              {t("ask.placeholder")}
            </span>
            <span
              aria-hidden="true"
              className="flex size-[38px] shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
            >
              <ArrowUp className="size-[18px]" />
            </span>
          </button>
        </div>
      )}
      <nav
        aria-label={t("mobileNav.label")}
        className="flex items-stretch pb-[26px] pt-[9px]"
      >
        {tabs.map((tab) => {
          const active = tab.match(path);
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.to)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex min-h-[44px] flex-1 flex-col items-center justify-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                active ? "text-primary" : "text-muted-foreground",
              )}
            >
              <Icon
                aria-hidden="true"
                className="size-[21px]"
                strokeWidth={active ? 2.2 : 1.8}
              />
              <span
                className={cn(
                  "text-[10.5px]",
                  active ? "font-semibold" : "font-medium",
                )}
              >
                {t(tab.labelKey)}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
